import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { WO_TYPE_COLORS, statusColors, priorityColors, STATUSES } from '../utils/constants';
import { FiPlus, FiSearch, FiTool, FiDownload, FiCalendar, FiX } from 'react-icons/fi';
import { playClick, playSuccess } from '../utils/sounds';
import WorkOrderCreateModal from '../components/WorkOrderCreateModal';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function WorkOrders() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('due_date');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const LIMIT = 50;

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearchDebounced(search); setPage(1); }, 150);
    return () => clearTimeout(t);
  }, [search]);

  const loadIssues = () => {
    if (issues.length === 0) setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT, sort: sortBy });
    if (statusFilter !== 'All') params.set('status', statusFilter);
    if (typeFilter !== 'All') params.set('type', typeFilter);
    if (searchDebounced.trim()) params.set('search', searchDebounced.trim());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    authFetch(`/api/issues?${params.toString()}`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(data => {
        if (data && data.data) {
          setIssues(data.data);
          setTotal(data.total);
          setTotalPages(data.totalPages);
        } else {
          setIssues(Array.isArray(data) ? data : []);
        }
        setLoading(false);
      })
      .catch(() => { setIssues([]); setLoading(false); });
  };

  useEffect(() => { loadIssues(); }, [page, statusFilter, typeFilter, searchDebounced, dateFrom, dateTo, sortBy]);

  const filtered = issues;

  if (loading) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div className="skeleton" style={{ width: 160, height: 28, marginBottom: 8 }} />
            <div className="skeleton" style={{ width: 120, height: 14 }} />
          </div>
          <div className="skeleton" style={{ width: 150, height: 40, borderRadius: 10 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div className="skeleton" style={{ width: 200, height: 36 }} />
          <div className="skeleton" style={{ width: 220, height: 36 }} />
          <div className="skeleton" style={{ width: 180, height: 36 }} />
        </div>
        {[...Array(6)].map((_, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
            background: 'var(--bg-card)', borderRadius: 12, marginBottom: 8,
            border: '1px solid var(--border)'
          }}>
            <div className="skeleton" style={{ width: 50, height: 20, borderRadius: 5 }} />
            <div style={{ flex: 1 }}>
              <div className="skeleton" style={{ width: '60%', height: 16, marginBottom: 6 }} />
              <div className="skeleton" style={{ width: '35%', height: 12 }} />
            </div>
            <div className="skeleton" style={{ width: 60, height: 22, borderRadius: 5 }} />
            <div className="skeleton" style={{ width: 80, height: 14 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>Work Orders</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-secondary)' }}>{total} total work orders</p>
        </div>
        <div style={{ position: 'relative' }}>
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => {
              const params = new URLSearchParams({ format: 'xlsx' });
              if (statusFilter !== 'All') params.set('status', statusFilter);
              if (typeFilter !== 'All') params.set('type', typeFilter);
              authFetch(`/api/reports/work-orders?${params.toString()}`)
                .then(r => r.blob())
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'work-orders.xlsx';
                  document.body.appendChild(a); a.click();
                  document.body.removeChild(a); URL.revokeObjectURL(url);
                });
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
              borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-card)',
              cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#16a34a'
            }}
          ><FiDownload size={14} /> Export</motion.button>
        </div>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={() => setShowCreateModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
            borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff',
            cursor: 'pointer', fontSize: 14, fontWeight: 600, boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
            marginLeft: 'auto'
          }}
        ><FiPlus size={16} /> New Notification</motion.button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" placeholder="Search work orders..." value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px 10px 34px', borderRadius: 10, border: '1px solid var(--border)',
              fontSize: 14, outline: 'none', boxSizing: 'border-box',
              background: 'var(--bg-input)', color: 'var(--text-primary)', transition: 'border-color 0.2s'
            }}
            onFocus={e => e.target.style.borderColor = '#2563eb'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {['All', 'PM01', 'PM02', 'PM03'].map(t => (
            <button key={t} onClick={() => { playClick(); setTypeFilter(t); setPage(1); }}
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid',
                borderColor: typeFilter === t ? (WO_TYPE_COLORS[t] || '#2563eb') : 'var(--border)',
                background: typeFilter === t ? `${WO_TYPE_COLORS[t] || '#2563eb'}15` : 'var(--bg-card)',
                color: typeFilter === t ? (WO_TYPE_COLORS[t] || '#2563eb') : 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600
              }}
            >{t === 'All' ? 'All Types' : t}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {['All', ...STATUSES].map(s => (
            <button key={s} onClick={() => { playClick(); setStatusFilter(s); setPage(1); }}
              style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid',
                borderColor: statusFilter === s ? (statusColors[s] || '#2563eb') : 'var(--border)',
                background: statusFilter === s ? `${statusColors[s] || '#2563eb'}15` : 'var(--bg-card)',
                color: statusFilter === s ? (statusColors[s] || '#2563eb') : 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600
              }}
            >{s}</button>
          ))}
        </div>
      </div>

      {/* Date Range & Sort */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <FiCalendar size={13} style={{ color: 'var(--text-muted)' }} />
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>to</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }} />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
              style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              <FiX size={12} />
            </button>
          )}
        </div>
        <select value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }}>
          <option value="due_date">Due Date (Earliest)</option>
          <option value="due_date_desc">Due Date (Latest)</option>
          <option value="wo_number">WO Number (Latest)</option>
          <option value="created_at">Created (Latest)</option>
        </select>
      </div>

      {/* Work Order List */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
          <FiTool size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15 }}>No work orders found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.map((issue, idx) => (
            <div key={issue.id}
              onClick={() => { playClick(); navigate(`/work-orders/${issue.wo_number || issue.id}`); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                background: 'var(--bg-card)', borderRadius: 10, cursor: 'pointer',
                border: '1px solid var(--border)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)', flexWrap: 'wrap',
                touchAction: 'manipulation', transition: 'all 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(37,99,235,0.04)'; e.currentTarget.style.borderColor = 'rgba(37,99,235,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              {/* Left: WO number + type stacked */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, minWidth: 90, flexShrink: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '0.02em' }}>
                  {issue.wo_number}{issue.first_operation ? `/${issue.first_operation}` : ''}
                </span>
                <span style={{
                  padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                  color: '#fff', background: WO_TYPE_COLORS[issue.type] || '#6b7280'
                }}>{issue.type}</span>
              </div>

              {/* Center: Name + asset */}
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>{issue.name}</div>
                {issue.asset_name && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                    {issue.asset_tag && <span style={{ fontWeight: 600 }}>{issue.asset_tag}</span>}
                    {issue.asset_tag && ' — '}{issue.asset_name}
                  </div>
                )}
              </div>

              {/* Right: meta info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <span style={{
                  padding: '3px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                  background: `${priorityColors[issue.priority] || '#6b7280'}15`,
                  color: priorityColors[issue.priority] || '#6b7280'
                }}>{issue.priority}</span>

                {issue.due_date && (
                  <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', minWidth: 85 }}>{formatDate(issue.due_date)}</span>
                )}

                {issue.assigned_to_name && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{issue.assigned_to_name}</span>
                )}

                <ConfirmBadge issue={issue} />

                <span style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  background: statusColors[issue.status] || '#6b7280', color: '#fff'
                }}>{issue.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
          <button onClick={() => setPage(1)} disabled={page === 1}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>First</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
            Page {page} of {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>Next</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
            style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1, fontSize: 12, fontWeight: 600 }}>Last</button>
        </div>
      )}

      {showCreateModal && <WorkOrderCreateModal onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); setPage(1); setLoading(true); }} />}
    </div>
  );
}

const CONF_STYLES = {
  CNF: { background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' },
  PCNF: { background: '#fef9c3', color: '#ca8a04', border: '1px solid #fde68a' },
  NEW: { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }
};

function ConfirmBadge({ issue }) {
  if (issue.status === 'Complete' || issue.status === 'TECO') return null;
  const { op_count, op_confirmed_count, tc_count } = issue;
  let label = 'NEW';
  if (op_count > 0 && op_confirmed_count === op_count) label = 'CNF';
  else if (tc_count > 0) label = 'PCNF';
  const s = CONF_STYLES[label];
  return (
    <span style={{
      padding: '3px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
      letterSpacing: '0.5px', ...s
    }}>{label}</span>
  );
}
